/**
 * Extended drawing primitives — MT5-style tools.
 *
 * Adds: Vertical Line, Segment, Parallel Channel, Andrews' Pitchfork,
 *       Fib Extension, Fib Fan, Fib Time Zones, Fib Arcs,
 *       Triangle, Ellipse, Arrow Marker, Price Range / Measure, Text Label
 */

const HANDLE_RADIUS = 5;
const HANDLE_COLOR = '#6366f1';
const HANDLE_HIT = 8;
const HIT_TOLERANCE = 7;

function drawHandle(ctx, x, y, hpr, vpr) {
  ctx.beginPath();
  ctx.arc(x * hpr, y * vpr, HANDLE_RADIUS * hpr, 0, Math.PI * 2);
  ctx.fillStyle = HANDLE_COLOR; ctx.fill();
  ctx.strokeStyle = '#fff'; ctx.lineWidth = 1.5 * hpr; ctx.stroke();
}

function hexToRgba(hex, a) {
  const c = hex.replace('#', '');
  return `rgba(${parseInt(c.slice(0,2),16)},${parseInt(c.slice(2,4),16)},${parseInt(c.slice(4,6),16)},${a})`;
}

function distToSeg(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1, len2 = dx*dx + dy*dy;
  if (len2 === 0) return Math.hypot(px - x1, py - y1);
  let t = Math.max(0, Math.min(1, ((px-x1)*dx + (py-y1)*dy) / len2));
  return Math.hypot(px - (x1 + t*dx), py - (y1 + t*dy));
}

class Base {
  constructor() { this._chart = null; this._series = null; this._requestUpdate = null; this.isHovered = false; this.isSelected = false; this.lineWidth = undefined; this.lineStyle = undefined; }
  attached({ requestUpdate, chart, series }) { this._requestUpdate = requestUpdate; this._chart = chart; this._series = series; }
  detached() { this._requestUpdate = null; this._chart = null; this._series = null; }
  requestUpdate() { this._requestUpdate?.(); }
  hitTest() { return Infinity; }
  isNear(x, y) { return this.hitTest(x, y) <= HIT_TOLERANCE; }
  hitTestHandle() { return -1; }
  moveBy() {}
  moveHandle() {}
  _ts() { return this._chart?.timeScale(); }
  _toXY(logical, price) {
    return { x: this._ts()?.logicalToCoordinate(logical), y: this._series?.priceToCoordinate(price) };
  }
}

// ═══════════════════════════════════════════════════════════════════
// VERTICAL LINE
// ═══════════════════════════════════════════════════════════════════

class VLinePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      if (s._x == null) return;
      const xx = s._x * hpr;
      ctx.save();
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.3); ctx.lineWidth = 6 * hpr;
        ctx.beginPath(); ctx.moveTo(xx, 0); ctx.lineTo(xx, bs.height); ctx.stroke();
      }
      const lw = s.lineWidth || 1;
      ctx.strokeStyle = s.color; ctx.lineWidth = (s.isSelected ? lw + 1 : lw) * hpr;
      if (s.lineStyle === 1) ctx.setLineDash([6 * hpr, 4 * hpr]);
      else if (s.lineStyle === 2) ctx.setLineDash([2 * hpr, 3 * hpr]);
      else ctx.setLineDash([]);
      ctx.beginPath(); ctx.moveTo(xx, 0); ctx.lineTo(xx, bs.height); ctx.stroke();
      // Time label at bottom
      ctx.setLineDash([]);
      const lbl = s.timeLabel || '';
      if (lbl) {
        ctx.font = `${10 * vpr}px -apple-system,sans-serif`;
        const tw = ctx.measureText(lbl).width;
        ctx.fillStyle = s.color;
        ctx.fillRect(xx - tw/2 - 4*hpr, bs.height - 16*vpr, tw + 8*hpr, 16*vpr);
        ctx.fillStyle = '#fff'; ctx.textBaseline = 'middle';
        ctx.fillText(lbl, xx - tw/2, bs.height - 8*vpr);
      }
      if (s.isSelected) drawHandle(ctx, s._x, bs.height / vpr / 2, hpr, vpr);
      ctx.restore();
    })}};
  }
}

export class VerticalLinePrimitive extends Base {
  constructor(logical, options = {}) {
    super(); this.logical = logical; this.color = options.color || '#f59e0b';
    this.lineWidth = options.lineWidth ?? 1; this.lineStyle = options.lineStyle ?? 1;
    this.timeLabel = options.timeLabel || ''; this._x = null; this._view = new VLinePV(this);
  }
  updateAllViews() { if (this._ts()) this._x = this._ts().logicalToCoordinate(this.logical); }
  paneViews() { return [this._view]; }
  hitTest(x) { return this._x != null ? Math.abs(x - this._x) : Infinity; }
  hitTestHandle(x) { return this._x != null && Math.abs(x - this._x) < HANDLE_HIT ? 0 : -1; }
  moveBy(dL) { this.logical += dL; this._requestUpdate?.(); }
  moveHandle(i, logical) { this.logical = logical; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// PARALLEL CHANNEL (3 points)
// ═══════════════════════════════════════════════════════════════════

class ChannelPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2, p3 = s._p3;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      ctx.save();
      const x1 = p1.x*hpr, y1 = p1.y*vpr, x2 = p2.x*hpr, y2 = p2.y*vpr;
      // Baseline
      const drawLine = (ax, ay, bx, by, dash) => {
        if (dash) ctx.setLineDash(dash.map(d => d*hpr));
        else ctx.setLineDash([]);
        ctx.beginPath(); ctx.moveTo(ax, ay); ctx.lineTo(bx, by); ctx.stroke();
      };
            const _lw = s.lineWidth || 1.5;
      ctx.strokeStyle = s.color; ctx.lineWidth = (s.isSelected ? _lw + 0.5 : _lw) * vpr; ctx.lineCap = 'round';
      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.3); ctx.lineWidth = 6 * vpr;
        drawLine(x1, y1, x2, y2);
      }
      ctx.strokeStyle = s.color; ctx.lineWidth = (s.isSelected ? _lw + 0.5 : _lw) * vpr;
      drawLine(x1, y1, x2, y2);
      // Parallel line
      if (p3 && p3.y != null) {
        const dy = p3.y - p1.y;
        const py1 = y1 + dy * vpr, py2 = y2 + dy * vpr;
        drawLine(p1.x*hpr, py1, p2.x*hpr, py2);
        // Fill between
        ctx.fillStyle = hexToRgba(s.color, 0.06);
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
        ctx.lineTo(p2.x*hpr, py2); ctx.lineTo(p1.x*hpr, py1); ctx.closePath(); ctx.fill();
        // Median line
        ctx.strokeStyle = hexToRgba(s.color, 0.4); ctx.lineWidth = 1 * vpr;
        drawLine(p1.x*hpr, (y1+py1)/2, p2.x*hpr, (y2+py2)/2, [3, 3]);
      }
      if (s.isSelected) {
        drawHandle(ctx, p1.x, p1.y, hpr, vpr);
        drawHandle(ctx, p2.x, p2.y, hpr, vpr);
        if (p3 && p3.y != null) drawHandle(ctx, p3.x ?? p1.x, p3.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class ParallelChannelPrimitive extends Base {
  constructor(pt1, pt2, pt3, options = {}) {
    super(); this.pt1 = pt1; this.pt2 = pt2; this.pt3 = pt3;
    this.color = options.color || '#3b82f6';
    this._p1 = null; this._p2 = null; this._p3 = null; this._view = new ChannelPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
    if (this.pt3) this._p3 = this._toXY(this.pt3.logical || this.pt1.logical, this.pt3.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    const d1 = distToSeg(x, y, this._p1.x, this._p1.y, this._p2.x, this._p2.y);
    if (!this._p3) return d1;
    const dy = this._p3.y - this._p1.y;
    const d2 = distToSeg(x, y, this._p1.x, this._p1.y + dy, this._p2.x, this._p2.y + dy);
    return Math.min(d1, d2);
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x-this._p1.x, y-this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x-this._p2.x, y-this._p2.y) < HANDLE_HIT) return 1;
    if (this._p3 && Math.hypot(x-(this._p3.x??this._p1?.x), y-this._p3.y) < HANDLE_HIT) return 2;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical+dL, price: this.pt1.price+dP };
    this.pt2 = { logical: this.pt2.logical+dL, price: this.pt2.price+dP };
    if (this.pt3) this.pt3 = { logical: (this.pt3.logical||this.pt1.logical)+dL, price: this.pt3.price+dP };
    this._requestUpdate?.();
  }
  moveHandle(i, logical, price) {
    if (i === 0) this.pt1 = { logical, price };
    else if (i === 1) this.pt2 = { logical, price };
    else if (i === 2) this.pt3 = { logical, price };
    this._requestUpdate?.();
  }
  setPoint3(pt) { this.pt3 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// ANDREWS' PITCHFORK (3 points)
// ═══════════════════════════════════════════════════════════════════

class PitchforkPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2, p3 = s._p3;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      ctx.save();

      const x1=p1.x*hpr, y1=p1.y*vpr, x2=p2.x*hpr, y2=p2.y*vpr;
      // Median from p1 to midpoint of p2-p3
      if (p3 && p3.x != null) {
        const x3 = p3.x*hpr, y3 = p3.y*vpr;
        const mx = (x2+x3)/2, my = (y2+y3)/2;
        // Extend median
        const dx = mx-x1, dy = my-y1;
        const len = Math.hypot(dx, dy);
        const sc = len > 0 ? bs.width*2/len : 1;
        const ex = x1+dx*sc, ey = y1+dy*sc;

        // Fill
        ctx.fillStyle = hexToRgba(s.color, 0.04);
        ctx.beginPath(); ctx.moveTo(x2, y2); ctx.lineTo(x1+dx*sc, y1+dy*sc + (y2-my)*sc);
        ctx.lineTo(x1+dx*sc, y1+dy*sc + (y3-my)*sc); ctx.lineTo(x3, y3); ctx.closePath(); ctx.fill();

                { const _lw = s.lineWidth || 1.5; ctx.strokeStyle = s.color; ctx.lineWidth = (s.isSelected ? _lw + 0.5 : _lw) * vpr; ctx.lineCap = 'round'; }
        // Median line
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(ex, ey); ctx.stroke();
        // Prongs
        const extend = (ox, oy) => {
          const px = ox + (mx-x1)*sc/len*bs.width, py = oy + (my-y1)*sc/len*bs.width;
          ctx.beginPath(); ctx.moveTo(ox, oy); ctx.lineTo(px, py); ctx.stroke();
        };
        ctx.setLineDash([4*hpr, 3*hpr]);
        extend(x2, y2); extend(x3, y3);
        ctx.setLineDash([]);
      } else {
        { const _lw = s.lineWidth || 1.5; ctx.strokeStyle = s.color; ctx.lineWidth = _lw * vpr; }
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      }

      if (s.isSelected) {
        drawHandle(ctx, p1.x, p1.y, hpr, vpr);
        drawHandle(ctx, p2.x, p2.y, hpr, vpr);
        if (p3) drawHandle(ctx, p3.x, p3.y, hpr, vpr);
      }
      ctx.restore();
    })}};
  }
}

export class PitchforkPrimitive extends Base {
  constructor(pt1, pt2, pt3, options = {}) {
    super(); this.pt1 = pt1; this.pt2 = pt2; this.pt3 = pt3;
    this.color = options.color || '#8b5cf6';
    this._p1 = null; this._p2 = null; this._p3 = null; this._view = new PitchforkPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
    if (this.pt3) this._p3 = this._toXY(this.pt3.logical, this.pt3.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    let d = distToSeg(x, y, this._p1.x, this._p1.y, this._p2.x, this._p2.y);
    if (this._p3) d = Math.min(d, distToSeg(x, y, this._p1.x, this._p1.y, this._p3.x, this._p3.y));
    return d;
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x-this._p1.x, y-this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x-this._p2.x, y-this._p2.y) < HANDLE_HIT) return 1;
    if (this._p3 && Math.hypot(x-this._p3.x, y-this._p3.y) < HANDLE_HIT) return 2;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical+dL, price: this.pt1.price+dP };
    this.pt2 = { logical: this.pt2.logical+dL, price: this.pt2.price+dP };
    if (this.pt3) this.pt3 = { logical: this.pt3.logical+dL, price: this.pt3.price+dP };
    this._requestUpdate?.();
  }
  moveHandle(i, logical, price) {
    if (i===0) this.pt1 = {logical, price}; else if (i===1) this.pt2 = {logical, price}; else this.pt3 = {logical, price};
    this._requestUpdate?.();
  }
  setPoint3(pt) { this.pt3 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// FIBONACCI EXTENSION (3 points)
// ═══════════════════════════════════════════════════════════════════

/**
 * MT5 Fib Extension: 3 points (A, B, C).
 *
 * A→B = the measured move (e.g. swing low → swing high)
 * C   = the retracement point (pullback)
 * Levels project from C in the direction of A→B:
 *   Level price = C + (B - A) * ratio
 *
 * Renders:
 *  - A→B→C zigzag connecting lines
 *  - Horizontal extension levels from C's x-position rightward
 *  - Zone fills between levels
 *  - Labels with % and price
 *  - Handles on A, B, C when selected
 */

const DEFAULT_FIB_EXT_LEVELS = [
  { level: 0,     color: '#808080', visible: true },
  { level: 0.236, color: '#f59e0b', visible: true },
  { level: 0.382, color: '#f97316', visible: true },
  { level: 0.5,   color: '#ef4444', visible: true },
  { level: 0.618, color: '#dc2626', visible: true },
  { level: 0.786, color: '#be185d', visible: true },
  { level: 1,     color: '#22c55e', visible: true },
  { level: 1.272, color: '#059669', visible: true },
  { level: 1.618, color: '#0891b2', visible: true },
  { level: 2,     color: '#2563eb', visible: true },
  { level: 2.618, color: '#7c3aed', visible: true },
  { level: 4.236, color: '#a855f7', visible: true },
];

class FibExtPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const pA = s._pA, pB = s._pB, pC = s._pC;
      const lines = s._lines;
      const W = bs.width;

      ctx.save();

      // ── A→B→C zigzag ──
      if (pA && pB && pA.x != null && pB.x != null) {
        ctx.strokeStyle = hexToRgba(s.color, 0.6);
        ctx.lineWidth = 1.5 * vpr; ctx.lineCap = 'round';
        ctx.setLineDash([]);
        ctx.beginPath();
        ctx.moveTo(pA.x * hpr, pA.y * vpr);
        ctx.lineTo(pB.x * hpr, pB.y * vpr);
        if (pC && pC.x != null) ctx.lineTo(pC.x * hpr, pC.y * vpr);
        ctx.stroke();

        // Dots on A, B, C
        [pA, pB, pC].filter(Boolean).forEach(p => {
          if (p.x == null) return;
          ctx.fillStyle = s.color;
          ctx.beginPath(); ctx.arc(p.x * hpr, p.y * vpr, 3 * hpr, 0, Math.PI * 2); ctx.fill();
        });

        // Labels A, B, C
        ctx.font = `bold ${10 * vpr}px -apple-system,sans-serif`;
        ctx.fillStyle = s.color; ctx.textBaseline = 'bottom';
        ctx.fillText('A', pA.x * hpr - 4 * hpr, pA.y * vpr - 6 * vpr);
        ctx.fillText('B', pB.x * hpr - 4 * hpr, pB.y * vpr - 6 * vpr);
        if (pC && pC.x != null) ctx.fillText('C', pC.x * hpr - 4 * hpr, pC.y * vpr - 6 * vpr);
      }

      // ── Extension levels ──
      if (lines && lines.length && pC && pC.x != null) {
        const startX = pC.x * hpr;

        // Zone fills between levels
        for (let i = 0; i < lines.length - 1; i++) {
          const y1 = lines[i].y * vpr, y2 = lines[i + 1].y * vpr;
          const lv = lines[i].level;
          const fillAlpha = lv >= 1 ? 0.05 : 0.03;
          ctx.fillStyle = hexToRgba(lines[i].color || s.color, fillAlpha);
          ctx.fillRect(startX, Math.min(y1, y2), W - startX, Math.abs(y2 - y1));
        }

        // Level lines + labels
        for (const l of lines) {
          const yy = l.y * vpr;
          const lvColor = l.color || s.color;
          const isKey = l.level === 0 || l.level === 1 || l.level === 1.618 || l.level === 2.618;

          // Line from C's x to right edge
          ctx.strokeStyle = hexToRgba(lvColor, s.isHovered || s.isSelected ? 0.85 : 0.55);
          ctx.lineWidth = (isKey ? 1.5 : 1) * vpr;
          ctx.setLineDash(l.level >= 1 ? [] : [4 * hpr, 3 * hpr]);
          ctx.beginPath(); ctx.moveTo(startX, yy); ctx.lineTo(W, yy); ctx.stroke();

          // Label
          const pct = `${(l.level * 100).toFixed(1)}%`;
          const priceStr = l.price.toFixed(5);
          ctx.font = `${10 * vpr}px -apple-system,sans-serif`;
          const labelText = `${pct}  (${priceStr})`;
          const tw = ctx.measureText(labelText).width;

          // Label background
          ctx.fillStyle = hexToRgba(lvColor, 0.12);
          const lx = W - tw - 14 * hpr;
          ctx.fillRect(lx, yy - 13 * vpr, tw + 8 * hpr, 15 * vpr);

          ctx.fillStyle = hexToRgba(lvColor, 0.9);
          ctx.textBaseline = 'middle';
          ctx.fillText(labelText, lx + 4 * hpr, yy - 5.5 * vpr);
        }
      }

      // ── Handles ──
      if (s.isSelected) {
        if (pA && pA.x != null) drawHandle(ctx, pA.x, pA.y, hpr, vpr);
        if (pB && pB.x != null) drawHandle(ctx, pB.x, pB.y, hpr, vpr);
        if (pC && pC.x != null) drawHandle(ctx, pC.x, pC.y, hpr, vpr);
      }

      ctx.restore();
    })}};
  }
}

export class FibExtensionPrimitive extends Base {
  constructor(pt1, pt2, pt3, options = {}) {
    super();
    this.pt1 = pt1; // A — start of measured move
    this.pt2 = pt2; // B — end of measured move
    this.pt3 = pt3; // C — retracement point (projection origin)
    this.color = options.color || '#f59e0b';
    this.customLevels = options.levels || DEFAULT_FIB_EXT_LEVELS.map(l => ({ ...l }));
    this._pA = null; this._pB = null; this._pC = null;
    this._lines = [];
    this._view = new FibExtPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._pA = this._toXY(this.pt1.logical, this.pt1.price);
    this._pB = this._toXY(this.pt2.logical, this.pt2.price);
    if (this.pt3) {
      this._pC = this._toXY(this.pt3.logical, this.pt3.price);
      const range = this.pt2.price - this.pt1.price;
      this._lines = this.customLevels
        .filter(l => l.visible !== false)
        .map(cl => {
          const price = this.pt3.price + range * cl.level;
          return { level: cl.level, price, y: this._series.priceToCoordinate(price), color: cl.color };
        });
    } else {
      this._pC = null;
      this._lines = [];
    }
  }
  setLevels(levels) { this.customLevels = levels; this._requestUpdate?.(); }
  getLevels() { return this.customLevels.map(l => ({ ...l })); }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    // Hit on zigzag lines or on level lines
    let min = Infinity;
    if (this._pA && this._pB) min = Math.min(min, distToSeg(x, y, this._pA.x, this._pA.y, this._pB.x, this._pB.y));
    if (this._pB && this._pC) min = Math.min(min, distToSeg(x, y, this._pB.x, this._pB.y, this._pC.x, this._pC.y));
    if (this._lines.length) min = Math.min(min, ...this._lines.map(l => Math.abs(y - l.y)));
    return min;
  }
  hitTestHandle(x, y) {
    if (this._pA && Math.hypot(x - this._pA.x, y - this._pA.y) < HANDLE_HIT) return 0;
    if (this._pB && Math.hypot(x - this._pB.x, y - this._pB.y) < HANDLE_HIT) return 1;
    if (this._pC && Math.hypot(x - this._pC.x, y - this._pC.y) < HANDLE_HIT) return 2;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = { logical: this.pt1.logical + dL, price: this.pt1.price + dP };
    this.pt2 = { logical: this.pt2.logical + dL, price: this.pt2.price + dP };
    if (this.pt3) this.pt3 = { logical: this.pt3.logical + dL, price: this.pt3.price + dP };
    this._requestUpdate?.();
  }
  moveHandle(i, logical, price) {
    if (i === 0) this.pt1 = { logical, price };
    else if (i === 1) this.pt2 = { logical, price };
    else if (i === 2) this.pt3 = { logical, price };
    this._requestUpdate?.();
  }
  setPoint(n, pt) {
    if (n === 2) this.pt2 = pt;
    if (n === 3) this.pt3 = pt;
    this._requestUpdate?.();
  }
  setPoint3(pt) { this.pt3 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// FIBONACCI FAN (2 points)
// ═══════════════════════════════════════════════════════════════════

const FIB_FAN_LEVELS = [0.236, 0.382, 0.5, 0.618, 0.786];

class FibFanPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      ctx.save();
      const x1=p1.x*hpr, y1=p1.y*vpr, x2=p2.x*hpr, y2=p2.y*vpr;
      const dy = y2 - y1, dx = x2 - x1;

      for (const lv of FIB_FAN_LEVELS) {
        const ey = y1 + dy * lv;
        const sc = dx !== 0 ? bs.width * 3 / Math.abs(dx) : 1;
        const ex = x1 + dx * sc, eey = y1 + (ey - y1) * sc;
        ctx.strokeStyle = hexToRgba(s.color, 0.5);
        ctx.lineWidth = vpr; ctx.setLineDash([3*hpr, 2*hpr]);
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(ex, eey); ctx.stroke();
        // Label
        ctx.font = `${9*vpr}px -apple-system,sans-serif`;
        ctx.fillStyle = hexToRgba(s.color, 0.6);
        ctx.fillText(`${(lv*100).toFixed(1)}%`, x2 + 5*hpr, y1 + (ey-y1) * (x2-x1 !== 0 ? 1 : 0));
      }
      // Main line
      ctx.setLineDash([]);
      ctx.strokeStyle = s.color; ctx.lineWidth = 1.5 * vpr;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      if (s.isSelected) { drawHandle(ctx, p1.x, p1.y, hpr, vpr); drawHandle(ctx, p2.x, p2.y, hpr, vpr); }
      ctx.restore();
    })}};
  }
}

export class FibFanPrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super(); this.pt1 = pt1; this.pt2 = pt2; this.color = options.color || '#f59e0b';
    this._p1 = null; this._p2 = null; this._view = new FibFanPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    if (this.pt2) this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    return distToSeg(x, y, this._p1.x, this._p1.y, this._p2.x, this._p2.y);
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x-this._p1.x, y-this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x-this._p2.x, y-this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = {logical:this.pt1.logical+dL, price:this.pt1.price+dP};
    this.pt2 = {logical:this.pt2.logical+dL, price:this.pt2.price+dP};
    this._requestUpdate?.();
  }
  moveHandle(i, logical, price) {
    if (i===0) this.pt1 = {logical, price}; else this.pt2 = {logical, price};
    this._requestUpdate?.();
  }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// FIBONACCI TIME ZONES (2 points)
// ═══════════════════════════════════════════════════════════════════

const FIB_SEQUENCE = [1, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89];

class FibTimePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      if (s._verticals.length === 0) return;
      ctx.save();
      for (let i = 0; i < s._verticals.length; i++) {
        const v = s._verticals[i];
        if (v.x == null) continue;
        const xx = v.x * hpr;
        if (xx < 0 || xx > bs.width) continue;
        ctx.strokeStyle = hexToRgba(s.color, 0.4 + (i < 3 ? 0.2 : 0));
        ctx.lineWidth = vpr; ctx.setLineDash([3*hpr, 2*hpr]);
        ctx.beginPath(); ctx.moveTo(xx, 0); ctx.lineTo(xx, bs.height); ctx.stroke();
        ctx.font = `${9*vpr}px -apple-system,sans-serif`;
        ctx.fillStyle = hexToRgba(s.color, 0.7);
        ctx.fillText(v.label, xx + 3*hpr, 12*vpr);
      }
      ctx.restore();
    })}};
  }
}

export class FibTimeZonesPrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super(); this.pt1 = pt1; this.pt2 = pt2; this.color = options.color || '#f59e0b';
    this._verticals = []; this._view = new FibTimePV(this);
  }
  updateAllViews() {
    if (!this._chart || !this.pt1 || !this.pt2) { this._verticals = []; return; }
    const ts = this._ts();
    const span = Math.abs(this.pt2.logical - this.pt1.logical) || 1;
    const start = Math.min(this.pt1.logical, this.pt2.logical);
    let acc = 0;
    this._verticals = FIB_SEQUENCE.map(n => {
      acc += n;
      const logical = start + acc * span;
      return { x: ts.logicalToCoordinate(logical), label: `${acc}` };
    });
  }
  paneViews() { return [this._view]; }
  hitTest(x) {
    if (!this._verticals.length) return Infinity;
    return Math.min(...this._verticals.filter(v => v.x != null).map(v => Math.abs(x - v.x)));
  }
  moveBy(dL) {
    this.pt1 = {logical: this.pt1.logical+dL, price: this.pt1.price};
    this.pt2 = {logical: this.pt2.logical+dL, price: this.pt2.price};
    this._requestUpdate?.();
  }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// FIBONACCI ARCS (2 points)
// ═══════════════════════════════════════════════════════════════════

const FIB_ARC_LEVELS = [0.236, 0.382, 0.5, 0.618, 0.786];

class FibArcPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      ctx.save();
      const x1=p1.x*hpr, y1=p1.y*vpr, x2=p2.x*hpr, y2=p2.y*vpr;
      const dist = Math.hypot(x2-x1, y2-y1);

      for (const lv of FIB_ARC_LEVELS) {
        const r = dist * lv;
        ctx.strokeStyle = hexToRgba(s.color, 0.45);
        ctx.lineWidth = vpr; ctx.setLineDash([3*hpr, 2*hpr]);
        ctx.beginPath(); ctx.arc(x2, y2, r, 0, Math.PI * 2); ctx.stroke();
        ctx.font = `${9*vpr}px -apple-system,sans-serif`;
        ctx.fillStyle = hexToRgba(s.color, 0.6);
        ctx.fillText(`${(lv*100).toFixed(1)}%`, x2 + r + 3*hpr, y2);
      }
      // Main line
      ctx.setLineDash([]);
      ctx.strokeStyle = s.color; ctx.lineWidth = 1.5*vpr;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      if (s.isSelected) { drawHandle(ctx, p1.x, p1.y, hpr, vpr); drawHandle(ctx, p2.x, p2.y, hpr, vpr); }
      ctx.restore();
    })}};
  }
}

export class FibArcsPrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super(); this.pt1 = pt1; this.pt2 = pt2; this.color = options.color || '#f59e0b';
    this._p1 = null; this._p2 = null; this._view = new FibArcPV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    if (this.pt2) this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    return distToSeg(x, y, this._p1.x, this._p1.y, this._p2.x, this._p2.y);
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x-this._p1.x, y-this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x-this._p2.x, y-this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = {logical:this.pt1.logical+dL, price:this.pt1.price+dP};
    this.pt2 = {logical:this.pt2.logical+dL, price:this.pt2.price+dP};
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) { if (i===0) this.pt1={logical:l,price:p}; else this.pt2={logical:l,price:p}; this._requestUpdate?.(); }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// TRIANGLE (3 points)
// ═══════════════════════════════════════════════════════════════════

class TrianglePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const pts = [s._p1, s._p2, s._p3].filter(p => p && p.x != null);
      if (pts.length < 2) return;
      ctx.save();
      const _lw = s.lineWidth || 1.5;
      ctx.fillStyle = hexToRgba(s.color, s.isHovered || s.isSelected ? 0.12 : 0.06);
      ctx.strokeStyle = s.color; ctx.lineWidth = (s.isSelected ? _lw + 0.5 : _lw) * vpr; ctx.lineCap = 'round';

      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.3); ctx.lineWidth = 6 * vpr;
        ctx.beginPath(); pts.forEach((p,i) => i===0 ? ctx.moveTo(p.x*hpr, p.y*vpr) : ctx.lineTo(p.x*hpr, p.y*vpr));
        if (pts.length >= 3) ctx.closePath(); ctx.stroke();
      }

      ctx.strokeStyle = s.color; ctx.lineWidth = (s.isSelected ? _lw + 0.5 : _lw) * vpr;
      ctx.beginPath();
      pts.forEach((p, i) => i === 0 ? ctx.moveTo(p.x*hpr, p.y*vpr) : ctx.lineTo(p.x*hpr, p.y*vpr));
      if (pts.length >= 3) { ctx.closePath(); ctx.fill(); }
      ctx.stroke();

      if (s.isSelected) pts.forEach(p => drawHandle(ctx, p.x, p.y, hpr, vpr));
      ctx.restore();
    })}};
  }
}

export class TrianglePrimitive extends Base {
  constructor(pt1, pt2, pt3, options = {}) {
    super(); this.pt1 = pt1; this.pt2 = pt2; this.pt3 = pt3;
    this.color = options.color || '#22d3ee';
    this._p1 = null; this._p2 = null; this._p3 = null; this._view = new TrianglePV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    if (this.pt2) this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
    if (this.pt3) this._p3 = this._toXY(this.pt3.logical, this.pt3.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    const pts = [this._p1, this._p2, this._p3].filter(Boolean);
    if (pts.length < 2) return Infinity;
    let min = Infinity;
    for (let i = 0; i < pts.length; i++) {
      const j = (i+1) % pts.length;
      min = Math.min(min, distToSeg(x, y, pts[i].x, pts[i].y, pts[j].x, pts[j].y));
    }
    return min;
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x-this._p1.x, y-this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x-this._p2.x, y-this._p2.y) < HANDLE_HIT) return 1;
    if (this._p3 && Math.hypot(x-this._p3.x, y-this._p3.y) < HANDLE_HIT) return 2;
    return -1;
  }
  moveBy(dL, dP) {
    [this.pt1, this.pt2, this.pt3].filter(Boolean).forEach(p => { p.logical += dL; p.price += dP; });
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) {
    if (i===0) this.pt1={logical:l,price:p}; else if (i===1) this.pt2={logical:l,price:p}; else this.pt3={logical:l,price:p};
    this._requestUpdate?.();
  }
  setPoint(n, pt) { if (n===2) this.pt2=pt; if (n===3) this.pt3=pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// ELLIPSE (2 points — bounding box corners)
// ═══════════════════════════════════════════════════════════════════

class EllipsePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      ctx.save();
      const cx = ((p1.x+p2.x)/2)*hpr, cy = ((p1.y+p2.y)/2)*vpr;
      const rx = Math.abs(p2.x-p1.x)/2*hpr, ry = Math.abs(p2.y-p1.y)/2*vpr;
      if (rx < 1 || ry < 1) { ctx.restore(); return; }

      const _lw = s.lineWidth || 1.5;
      ctx.fillStyle = hexToRgba(s.color, s.isHovered || s.isSelected ? 0.12 : 0.06);
      ctx.beginPath(); ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI*2); ctx.fill();

      if (s.isHovered || s.isSelected) {
        ctx.strokeStyle = hexToRgba(s.color, 0.3); ctx.lineWidth = 5*vpr;
        ctx.beginPath(); ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI*2); ctx.stroke();
      }
      ctx.strokeStyle = s.color; ctx.lineWidth = (s.isSelected ? _lw + 0.5 : _lw) * vpr;
      ctx.beginPath(); ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI*2); ctx.stroke();

      if (s.isSelected) { drawHandle(ctx, p1.x, p1.y, hpr, vpr); drawHandle(ctx, p2.x, p2.y, hpr, vpr); }
      ctx.restore();
    })}};
  }
}

export class EllipsePrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super(); this.pt1 = pt1; this.pt2 = pt2; this.color = options.color || '#22d3ee';
    this._p1 = null; this._p2 = null; this._view = new EllipsePV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    if (this.pt2) this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    const cx = (this._p1.x+this._p2.x)/2, cy = (this._p1.y+this._p2.y)/2;
    const rx = Math.abs(this._p2.x-this._p1.x)/2 || 1, ry = Math.abs(this._p2.y-this._p1.y)/2 || 1;
    const d = ((x-cx)/rx)**2 + ((y-cy)/ry)**2;
    return d <= 1 ? 0 : Infinity;
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x-this._p1.x, y-this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x-this._p2.x, y-this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = {logical:this.pt1.logical+dL, price:this.pt1.price+dP};
    this.pt2 = {logical:this.pt2.logical+dL, price:this.pt2.price+dP};
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) { if (i===0) this.pt1={logical:l,price:p}; else this.pt2={logical:l,price:p}; this._requestUpdate?.(); }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// ARROW MARKER (1 point)
// ═══════════════════════════════════════════════════════════════════

class ArrowPV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p = s._p;
      if (!p || p.x == null) return;
      const xx = p.x * hpr, yy = p.y * vpr;
      const sz = 12 * hpr;
      ctx.save();
      ctx.fillStyle = s.color;
      if (s.direction === 'up') {
        ctx.beginPath(); ctx.moveTo(xx, yy - sz); ctx.lineTo(xx - sz*0.6, yy); ctx.lineTo(xx + sz*0.6, yy); ctx.closePath(); ctx.fill();
      } else {
        ctx.beginPath(); ctx.moveTo(xx, yy + sz); ctx.lineTo(xx - sz*0.6, yy); ctx.lineTo(xx + sz*0.6, yy); ctx.closePath(); ctx.fill();
      }
      if (s.isSelected) drawHandle(ctx, p.x, p.y, hpr, vpr);
      ctx.restore();
    })}};
  }
}

export class ArrowMarkerPrimitive extends Base {
  constructor(pt, options = {}) {
    super(); this.pt = pt; this.color = options.color || '#22c55e';
    this.direction = options.direction || 'up';
    this._p = null; this._view = new ArrowPV(this);
  }
  updateAllViews() {
    if (this._chart && this._series) this._p = this._toXY(this.pt.logical, this.pt.price);
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) { return this._p ? Math.hypot(x-this._p.x, y-this._p.y) : Infinity; }
  hitTestHandle(x, y) { return this._p && Math.hypot(x-this._p.x, y-this._p.y) < HANDLE_HIT ? 0 : -1; }
  moveBy(dL, dP) { this.pt = {logical:this.pt.logical+dL, price:this.pt.price+dP}; this._requestUpdate?.(); }
  moveHandle(i, l, p) { this.pt = {logical:l, price:p}; this._requestUpdate?.(); }
}

// ═══════════════════════════════════════════════════════════════════
// PRICE RANGE / MEASURE (2 points)
// ═══════════════════════════════════════════════════════════════════

class MeasurePV {
  constructor(s) { this._s = s; }
  update() {}
  renderer() {
    const s = this._s;
    return { draw: (t) => { t.useBitmapCoordinateSpace(({ context: ctx, bitmapSize: bs, horizontalPixelRatio: hpr, verticalPixelRatio: vpr }) => {
      const p1 = s._p1, p2 = s._p2;
      if (!p1 || !p2 || p1.x == null || p2.x == null) return;
      ctx.save();
      const x1=p1.x*hpr, y1=p1.y*vpr, x2=p2.x*hpr, y2=p2.y*vpr;

      // Box
      ctx.fillStyle = s.priceDiff >= 0 ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)';
      ctx.fillRect(Math.min(x1,x2), Math.min(y1,y2), Math.abs(x2-x1), Math.abs(y2-y1));
      ctx.strokeStyle = s.priceDiff >= 0 ? '#22c55e' : '#ef4444';
      ctx.lineWidth = vpr; ctx.setLineDash([3*hpr, 2*hpr]);
      ctx.strokeRect(Math.min(x1,x2), Math.min(y1,y2), Math.abs(x2-x1), Math.abs(y2-y1));

      // Info label
      ctx.setLineDash([]);
      const pips = Math.abs(s.priceDiff * 100000).toFixed(1);
      const pct = s.pt1.price !== 0 ? ((s.priceDiff / s.pt1.price) * 100).toFixed(2) : '0';
      const bars = Math.abs(s.pt2.logical - s.pt1.logical);
      const sign = s.priceDiff >= 0 ? '+' : '';
      const label = `${sign}${s.priceDiff.toFixed(5)}  (${sign}${pct}%)  |  ${pips} pips  |  ${bars} bars`;

      ctx.font = `bold ${11*vpr}px -apple-system,sans-serif`;
      const tw = ctx.measureText(label).width;
      const lx = (x1+x2)/2 - tw/2, ly = Math.min(y1,y2) - 22*vpr;

      ctx.fillStyle = s.priceDiff >= 0 ? 'rgba(34,197,94,0.9)' : 'rgba(239,68,68,0.9)';
      ctx.beginPath(); ctx.roundRect(lx - 6*hpr, ly - 2*vpr, tw + 12*hpr, 18*vpr, 4*hpr); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.textBaseline = 'middle';
      ctx.fillText(label, lx, ly + 7*vpr);

      if (s.isSelected) { drawHandle(ctx, p1.x, p1.y, hpr, vpr); drawHandle(ctx, p2.x, p2.y, hpr, vpr); }
      ctx.restore();
    })}};
  }
}

export class MeasurePrimitive extends Base {
  constructor(pt1, pt2, options = {}) {
    super(); this.pt1 = pt1; this.pt2 = pt2; this.color = options.color || '#6366f1';
    this.priceDiff = 0; this._p1 = null; this._p2 = null; this._view = new MeasurePV(this);
  }
  updateAllViews() {
    if (!this._chart || !this._series) return;
    this._p1 = this._toXY(this.pt1.logical, this.pt1.price);
    if (this.pt2) {
      this._p2 = this._toXY(this.pt2.logical, this.pt2.price);
      this.priceDiff = this.pt2.price - this.pt1.price;
    }
  }
  paneViews() { return [this._view]; }
  hitTest(x, y) {
    if (!this._p1 || !this._p2) return Infinity;
    const r = { x: Math.min(this._p1.x, this._p2.x), y: Math.min(this._p1.y, this._p2.y),
      w: Math.abs(this._p2.x - this._p1.x), h: Math.abs(this._p2.y - this._p1.y) };
    return (x >= r.x && x <= r.x+r.w && y >= r.y && y <= r.y+r.h) ? 0 : Infinity;
  }
  hitTestHandle(x, y) {
    if (this._p1 && Math.hypot(x-this._p1.x, y-this._p1.y) < HANDLE_HIT) return 0;
    if (this._p2 && Math.hypot(x-this._p2.x, y-this._p2.y) < HANDLE_HIT) return 1;
    return -1;
  }
  moveBy(dL, dP) {
    this.pt1 = {logical:this.pt1.logical+dL, price:this.pt1.price+dP};
    this.pt2 = {logical:this.pt2.logical+dL, price:this.pt2.price+dP};
    this._requestUpdate?.();
  }
  moveHandle(i, l, p) { if (i===0) this.pt1={logical:l,price:p}; else this.pt2={logical:l,price:p}; this._requestUpdate?.(); }
  setPreviewPoint(pt) { this.pt2 = pt; this._requestUpdate?.(); }
}
